import React, { useEffect, useState } from 'react';
import { PlatformerLevel } from '../../../shared/CharacterTypes';
import { evaluatePortalGate } from '../../../shared/logic/portalGate';
import { EventBus } from '../../../shared/EventBus';
import { LevelRegistry } from '../../systems/map/LevelRegistry';
import { useGame } from '../contexts/GameContext';
import { useWindow } from '../framework/WindowManagerContext';
import { isMainGameDomain } from '../../../config/environment/environment';

/**
 * MapPickerOverlay - A Warcraft 3 inspired map selection screen.
 *
 * Features a retro dual-panel layout:
 * - Left: Scrollable map list
 * - Right: Detailed map information and attributes
 * - Bottom: Action buttons
 *
 * Level-gated: each map's required level (the destination zone's recommended
 * floor) is shown next to its name; the action button is disabled with a
 * clear "Requires level N+" label when the player is under-level.
 */

interface MapPickerOverlayProps {
  maps: PlatformerLevel[];
  onSelect: (mapId: string) => void;
  onCancel: () => void;
  isVisible: boolean;
  /** Current player level — drives the level gate. Undefined → treated as 1. */
  playerLevel?: number;
}

export const MapPickerOverlay: React.FC<MapPickerOverlayProps> = ({
  maps,
  onSelect,
  onCancel,
  isVisible,
  playerLevel,
}) => {
  const [selectedMapId, setSelectedMapId] = useState<string | null>(
    maps.length > 0 ? maps[0].id : null
  );

  const selectedMap = maps.find(m => m.id === selectedMapId);

  // Resolve the level gate for the selected map. The MapPicker warps directly
  // to a map id (targetMapId === the map itself), so the gate reads the
  // destination zone's recommended floor.
  const selectedGate = selectedMap
    ? evaluatePortalGate({ targetMapId: selectedMap.id }, playerLevel)
    : null;
  // Three-tier gate (on-ramp design §3): only 'block' locks the button —
  // 'warn' maps stay enterable (the warp path shows the soft-warn toast).
  const selectedLocked = !!selectedGate && selectedGate.gate === 'block';

  if (!isVisible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>SELECT MAP</h1>
        </div>

        {/* Content Area */}
        <div style={styles.content}>

          {/* Left Panel: Map List */}
          <div style={styles.leftPanel}>
            <div style={styles.panelTitle}>MAP LIST</div>
            <div style={styles.scrollArea}>
              {maps.map((map) => {
                const gate = evaluatePortalGate({ targetMapId: map.id }, playerLevel);
                const locked = gate.gate === 'block';
                const warn = gate.gate === 'warn';
                return (
                  <div
                    key={map.id}
                    onClick={() => setSelectedMapId(map.id)}
                    style={{
                      ...styles.mapListItem,
                      ...(locked ? styles.mapListItemLocked : {}),
                      backgroundColor: selectedMapId === map.id ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                      borderColor: selectedMapId === map.id ? '#ffd700' : 'rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <span style={{
                      color: selectedMapId === map.id ? '#ffd700' : (locked ? '#888' : '#fff'),
                      fontWeight: selectedMapId === map.id ? 'bold' : 'normal'
                    }}>
                      {locked ? '🔒 ' : ''}{map.name}
                    </span>
                    {gate.requiredLevel > 1 && (
                      <span style={locked ? styles.reqBadgeLocked : warn ? styles.reqBadgeWarn : styles.reqBadge}>
                        {warn ? '⚠ ' : ''}Lv {gate.requiredLevel}+
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Map Details */}
          <div style={styles.rightPanel}>
            {selectedMap ? (
              <>
                <div style={styles.detailHeader}>
                  <h2 style={styles.mapTitle}>{selectedMap.name}</h2>
                  <div style={styles.mapId}>{selectedMap.id}</div>
                </div>

                {/* Attributes Grid */}
                <div style={styles.statsGrid}>
                  <StatItem label="AUTHOR" value={selectedMap.metadata?.author || 'Unknown'} />
                  <StatItem label="DIFFICULTY" value={selectedMap.metadata?.difficulty || 'Normal'} />
                  <StatItem label="PLAYERS" value={selectedMap.metadata?.maxPlayers?.toString() || '1'} />
                  <StatItem
                    label="REQ. LEVEL"
                    value={selectedGate && selectedGate.requiredLevel > 1 ? `${selectedGate.requiredLevel}+` : 'None'}
                  />
                </div>

                {/* Description */}
                <div style={styles.descriptionBox}>
                  <div style={styles.sectionLabel}>DESCRIPTION</div>
                  <p style={styles.descriptionText}>
                    {selectedMap.metadata?.description || 'No description provided for this map.'}
                  </p>
                </div>

                {/* Tags */}
                {selectedMap.metadata?.tags && (
                  <div style={styles.tagsContainer}>
                    {selectedMap.metadata.tags.map(tag => (
                      <span key={tag} style={styles.tag}>#{tag}</span>
                    ))}
                  </div>
                )}

                {/* Map Dimensions */}
                <div style={styles.dimensions}>
                   Size: {selectedMap.width}x{selectedMap.height} tiles
                </div>
              </>
            ) : (
              <div style={styles.placeholder}>Select a map to view details</div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div style={styles.footer}>
          <button
            style={styles.cancelButton}
            onClick={onCancel}
            onMouseOver={(e) => (e.currentTarget.style.filter = 'brightness(1.2)')}
            onMouseOut={(e) => (e.currentTarget.style.filter = 'none')}
          >
            CANCEL
          </button>
          <button
            style={{
              ...styles.primaryButton,
              ...(selectedLocked ? styles.primaryButtonLocked : {}),
              opacity: selectedMapId && !selectedLocked ? 1 : 0.5,
              cursor: selectedMapId && !selectedLocked ? 'pointer' : 'not-allowed'
            }}
            disabled={!selectedMapId || selectedLocked}
            onClick={() => selectedMapId && !selectedLocked && onSelect(selectedMapId)}
            onMouseOver={(e) => selectedMapId && !selectedLocked && (e.currentTarget.style.filter = 'brightness(1.2)')}
            onMouseOut={(e) => (e.currentTarget.style.filter = 'none')}
            title={selectedLocked ? `Requires level ${selectedGate?.requiredLevel}+` : undefined}
          >
            {selectedLocked ? `REQUIRES LEVEL ${selectedGate?.requiredLevel}+` : 'CREATE GAME'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={styles.statItem}>
    <div style={styles.statLabel}>{label}</div>
    <div style={styles.statValue}>{value}</div>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    pointerEvents: 'auto' // Ensure overlay receives clicks
  },
  container: {
    width: '800px',
    height: '550px',
    background: 'linear-gradient(135deg, #1a1a1a, #0d0d0d)',
    border: '2px solid #3d3d3d',
    borderRadius: '12px',
    boxShadow: '0 0 50px rgba(0,0,0,1), inset 0 0 20px rgba(255,215,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    padding: '15px 25px',
    borderBottom: '1px solid #333',
    background: 'linear-gradient(to bottom, #252525, #1a1a1a)'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    color: '#ffd700',
    letterSpacing: '3px',
    textShadow: '2px 2px 2px #000',
    fontWeight: '900'
  },
  content: {
    flex: 1,
    display: 'flex',
    padding: '0'
  },
  leftPanel: {
    width: '280px',
    borderRight: '1px solid #333',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(0,0,0,0.2)'
  },
  panelTitle: {
    padding: '10px 15px',
    fontSize: '11px',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    backgroundColor: 'rgba(0,0,0,0.3)'
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '10px'
  },
  mapListItem: {
    padding: '12px 15px',
    marginBottom: '6px',
    borderRadius: '6px',
    border: '1px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  mapListItemLocked: {
    opacity: 0.7
  },
  reqBadge: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#8fd18f',
    background: 'rgba(46, 74, 45, 0.4)',
    border: '1px solid rgba(143, 209, 143, 0.4)',
    borderRadius: '10px',
    padding: '2px 8px',
    whiteSpace: 'nowrap'
  },
  reqBadgeWarn: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#e8c46a',
    background: 'rgba(110, 84, 20, 0.35)',
    border: '1px solid rgba(232, 196, 106, 0.5)',
    borderRadius: '10px',
    padding: '2px 8px',
    whiteSpace: 'nowrap'
  },
  reqBadgeLocked: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#e0a0a0',
    background: 'rgba(120, 40, 40, 0.4)',
    border: '1px solid rgba(224, 160, 160, 0.5)',
    borderRadius: '10px',
    padding: '2px 8px',
    whiteSpace: 'nowrap'
  },
  rightPanel: {
    flex: 1,
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    overflowY: 'auto'
  },
  detailHeader: {
    marginBottom: '20px',
    borderBottom: '1px solid rgba(255,215,0,0.2)',
    paddingBottom: '15px'
  },
  mapTitle: {
    margin: '0 0 5px 0',
    fontSize: '28px',
    color: '#ffd700',
    textShadow: '2px 2px 4px #000'
  },
  mapId: {
    fontSize: '11px',
    fontFamily: 'monospace',
    color: '#666'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    marginBottom: '25px'
  },
  statItem: {
    background: 'rgba(255,255,255,0.03)',
    padding: '10px 15px',
    borderRadius: '4px',
    border: '1px solid rgba(255,255,255,0.05)'
  },
  statLabel: {
    fontSize: '9px',
    color: '#ffd700',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '4px',
    opacity: 0.8
  },
  statValue: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#eee'
  },
  sectionLabel: {
    fontSize: '10px',
    color: '#ffd700',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '10px',
    fontWeight: 'bold'
  },
  descriptionBox: {
    marginBottom: '25px'
  },
  descriptionText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#ccc',
    margin: 0
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px'
  },
  tag: {
    fontSize: '11px',
    backgroundColor: '#333',
    color: '#aaa',
    padding: '3px 8px',
    borderRadius: '12px',
    border: '1px solid #444'
  },
  dimensions: {
    marginTop: 'auto',
    fontSize: '11px',
    color: '#444',
    textAlign: 'right'
  },
  footer: {
    padding: '20px 25px',
    borderTop: '1px solid #333',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '15px',
    background: 'linear-gradient(to top, #151515, #1d1d1d)'
  },
  cancelButton: {
    padding: '12px 25px',
    background: '#333',
    color: '#fff',
    border: '1px solid #444',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  primaryButton: {
    padding: '12px 35px',
    background: 'linear-gradient(to bottom, #d4af37, #996515)',
    color: '#000',
    border: '1px solid #ffd700',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    textShadow: '0 1px 0 rgba(255,255,255,0.3)',
    boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
  },
  primaryButtonLocked: {
    background: 'linear-gradient(to bottom, #5a4a3a, #3a2a1a)',
    border: '1px solid #6a5a4a',
    color: '#caa',
    textShadow: 'none'
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#444',
    fontStyle: 'italic'
  }
};

/**
 * The Map Picker is a dev surface (lane AN, 2026-10-08): it opens from the 🛠
 * launcher and the DEV BAR, never from a player key (M is the World Map), and
 * never on the public game domain. The server is the authority either way:
 * a portal-less `requestMapSwitch` is refused in production and from non-GMs
 * (MovementHandler.handleMapSwitch, `direct_transfer_forbidden`).
 */
export function isMapPickerAvailable(): boolean {
  return !isMainGameDomain();
}

/**
 * Engine Content wrapper (HUD fold, 2026-06-12) — the `mapPicker` window,
 * presentation:'overlay' (bare full-screen, no ToolWindow chrome). Opened by
 * the 🛠 launcher or the DEV-BAR 🧭 button (windowBridge.toggle('mapPicker')).
 * On the public game domain it renders nothing and closes itself.
 *
 * Warps via the EventBus 'requestMapSwitch' event — the proven path
 * (SceneEventManager listens; MapSystem portals emit the same event). The old
 * HUD mount dispatched onAction('requestMapSwitch') which fell into
 * ActionDispatcher's default "Unknown HUD action" warn — dead UI twice over.
 */
export const MapPickerContent: React.FC = () => {
  const { player } = useGame();
  const { closeWindow } = useWindow('mapPicker');
  const available = isMapPickerAvailable();
  useEffect(() => {
    if (!available) closeWindow();
  }, [available, closeWindow]);
  if (!available) return null;
  return (
    <MapPickerOverlay
      isVisible
      maps={LevelRegistry.getInstance().getAllLevels()}
      playerLevel={player?.level}
      onSelect={(mapId) => {
        EventBus.getInstance().emit('requestMapSwitch', { mapId });
        closeWindow();
      }}
      onCancel={closeWindow}
    />
  );
};
